// eyeq — maps-ingest contracts: прямой приём кадров maps-пайплайна в R2 с
// жёсткими гейтами НА ВХОДЕ (отказ = ни байта в R2, ни строки в каталоге).
// Два канала:
//   · PUT  /api/eyeq/maps/frame    — байты локального бэклога (гео уже вшито,
//                                    GPS обязателен);
//   · POST /api/eyeq/maps/fetchUrl — воркер сам качает оригинал с
//                                    lh3.googleusercontent.com (Google вырезает
//                                    GPS, поэтому гейт GPS выключен — гео этим
//                                    кадрам ещё предстоит).
import { z } from 'zod';
import { EyeqLocaleSchema } from './locales';
import { MeqCategorySchema, MeqSubCategorySchema } from './meta';

// Метаданные кадра едут в заголовке X-Eyeq-Maps-Meta (base64(JSON)), потому
// что тело запроса — сырые байты файла. Категория опциональна: бэклог делится
// на размеченные и неразмеченные кадры, обе половины должны заезжать.
export const EyeqMapsFrameMetaSchema = z
  .object({
    locale: EyeqLocaleSchema,
    filename: z.string().min(1).max(256),
    category: MeqCategorySchema.optional(),
    sub_category: MeqSubCategorySchema.optional(),
    /** Ссылка-источник (lh3) — для трассируемости в аудит-ленте. */
    source_url: z.string().max(2048).optional(),
    /** Локальный батч/партия — категориальная метка, не путь. */
    source_batch: z.string().max(64).optional(),
    /** true (дефолт) — кадр без GPS отбивается. fetchUrl всегда шлёт false. */
    require_gps: z.boolean().default(true),
    // Аннотация из локального реестра — опциональна; пишется в мету только
    // вместе с категорией. Пустые поля добирает QA/разметка позже.
    subject_name: z.string().max(200).optional(),
    disambiguation_details: z.string().max(500).optional(),
    indoor_outdoor: z.enum(['indoor', 'outdoor']).optional(),
    indoor_lighting: z.string().max(40).optional(),
    place_type: z.string().max(80).optional(),
    has_text: z.boolean().optional(),
    url: z.string().max(2048).optional(),
  })
  .refine((v) => !!v.category === !!v.sub_category, {
    message: 'category and sub_category come together',
  });
export type EyeqMapsFrameMeta = z.infer<typeof EyeqMapsFrameMetaSchema>;

// Категориальные причины отказа — реестр закрыт, фронтов у канала нет,
// потребитель — фидер-скрипт, который раскладывает отказы по счётчикам.
export const EyeqMapsRejectReasonSchema = z.enum([
  'no_device_exif',        // нет Make и Model — происхождение недоказуемо
  'not_phone',             // камера/дрон/экшн-камера по реестру phonegate
  'below_min_pixels',      // < 12 МП по маркеру SOF
  'dims_mismatch',         // размеры SOF ≠ EXIF — файл пережимали
  'edited_software',       // Software = известный редактор
  'zoom_excess',           // focal35/база модели > 2.0 или DigitalZoomRatio > 2.0
  'zoom_unverifiable',     // ни фокусного с базой, ни DigitalZoomRatio — жёсткое правило
  'gps_missing',           // require_gps и GPS-блока нет
  'device_second_duplicate', // то же (make, model, секунда съёмки) уже в каталоге
  'not_an_image',          // байты не распознаны как изображение
  'cell_unknown',          // пара категория|подкатегория не существует в таксономии
]);
export type EyeqMapsRejectReason = z.infer<typeof EyeqMapsRejectReasonSchema>;

export const EyeqMapsFrameResSchema = z.object({
  ok: z.boolean(),
  /** accepted — кадр в R2 и каталоге; duplicate — те же байты уже приняты
   *  (идемпотентный повтор, existing_asset_id указывает на строку);
   *  rejected — гейты не пройдены, НИЧЕГО не записано. */
  verdict: z.enum(['accepted', 'duplicate', 'rejected']),
  reasons: z.array(EyeqMapsRejectReasonSchema),
  asset_id: z.string().nullable(),
  existing_asset_id: z.string().nullable(),
  r2_key: z.string().nullable(),
  sha256: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  gps_present: z.boolean(),
  captured_at: z.string().nullable(),
  make: z.string(),
  model: z.string(),
});
export type EyeqMapsFrameRes = z.infer<typeof EyeqMapsFrameResSchema>;

// ── POST /api/eyeq/maps/fetchUrl ────────────────────────────────────────────
export const EyeqMapsFetchUrlReqSchema = z.object({
  /** Только lh3.googleusercontent.com (проверяется на воркере), суффикс =d
   *  уже в ссылке — качается как есть, без обработки. */
  photo_url: z.string().url().max(2048),
  locale: EyeqLocaleSchema,
  category: MeqCategorySchema.optional(),
  sub_category: MeqSubCategorySchema.optional(),
  filename: z.string().min(1).max(256).optional(),
  source_batch: z.string().max(64).optional(),
}).refine((v) => !!v.category === !!v.sub_category, {
  message: 'category and sub_category come together',
});
export type EyeqMapsFetchUrlReq = z.infer<typeof EyeqMapsFetchUrlReqSchema>;

// Ответ fetchUrl: либо вердикт кадра (fetch удался), либо fetch_failed со
// статусом источника — фидер по нему решает, падать ли на локальную копию.
export const EyeqMapsFetchUrlResSchema = z.union([
  EyeqMapsFrameResSchema.extend({ fetched_bytes: z.number().int() }),
  z.object({
    ok: z.literal(false),
    error: z.literal('fetch_failed'),
    status: z.number().int(),
  }),
]);
export type EyeqMapsFetchUrlRes = z.infer<typeof EyeqMapsFetchUrlResSchema>;

// ── POST /api/eyeq/maps/dedupCheck ──────────────────────────────────────────
// Предпроверка дубля ±2 с ДО передачи байтов (и до скачивания оригинала):
// фидер знает make/model/taken_at из пробы, а истина по дедупу живёт в D1.
// Ровно та же пара запросов, что в ingestMapsFrame — каталог + эталон
// отгрузок; ответ помечает кадры, которые заведомо отобьются.
export const EyeqMapsDedupCheckReqSchema = z.object({
  items: z.array(z.object({
    make: z.string().max(128),
    model: z.string().max(128),
    taken_at: z.string().max(32),
  })).min(1).max(200),
});
export type EyeqMapsDedupCheckReq = z.infer<typeof EyeqMapsDedupCheckReqSchema>;

export const EyeqMapsDedupCheckResSchema = z.object({
  // По позиции входа: true = дубль (кадр не стоит ни качать, ни слать).
  dup: z.array(z.boolean()),
});
export type EyeqMapsDedupCheckRes = z.infer<typeof EyeqMapsDedupCheckResSchema>;
